import { AccordionElement } from '@typedream/core';
import { getAboveNode } from '@udecode/plate-core';
import { memo } from 'react';
import { Dropdown, DropdownItem } from '../../../components/Dropdown';
import { setAccordionStyle } from '../../../nodes/accordion';
import { useTPlateEditorRef } from '../../../plate';
import { ELEMENT_ACCORDION } from '../../../pluginKeys';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import toolbarStyles from '../SideToolbar.module.css';

const illustrations = {
  plain: (
    <svg
      width="50"
      height="37"
      viewBox="0 0 50 37"
      fill="none"
      className="mr-4"
    >
      <path
        d="M50 6.98529H6.98529V29.7794H50V36.7647H0V0H50V6.98529Z"
        fill="var(--background-highlight-highlight)"
      />
      <path
        d="M20.8054 22.4306V15.5372H21.9752V18.9839V22.4306H20.8054ZM17.9436 19.5688V18.399H21.3903H24.837V19.5688H21.3903H17.9436Z"
        fill="#B1B5DC"
      />
    </svg>
  ),
  card: (
    <svg
      width="50"
      height="37"
      viewBox="0 0 50 37"
      fill="none"
      className="mr-4"
    >
      <path
        d="M0 29.7794V36.7647H50V0H0V6.98529H40.8088C42.8393 6.98529 44.4853 8.63131 44.4853 10.6618V26.1029C44.4853 28.1334 42.8393 29.7794 40.8088 29.7794H0Z"
        fill="var(--background-highlight-highlight)"
      />
      <path
        d="M32.5703 22.4306V15.5372H33.7401V18.9839V22.4306H32.5703ZM29.7085 19.5688V18.399H33.1552H36.6019V19.5688H33.1552H29.7085Z"
        fill="#B1B5DC"
      />
    </svg>
  ),
  button: (
    <svg
      width="50"
      height="37"
      viewBox="0 0 50 37"
      fill="none"
      className="mr-4"
    >
      <path
        d="M0 29.7794V36.7647H50V0H0V6.98529H33.0882C39.3827 6.98529 44.4853 12.0879 44.4853 18.3824C44.4853 24.6768 39.3827 29.7794 33.0882 29.7794H0Z"
        fill="var(--background-highlight-highlight)"
      />
      <path
        d="M36.0864 16.7236L33.4559 19.3541L30.8254 16.7236L29.959 17.5901L33.4559 21.087L36.9529 17.5901L36.0864 16.7236Z"
        fill="#B1B5DC"
      />
    </svg>
  ),
};

const styleOptions: NonNullable<AccordionElement['style']>[] = [
  'plain',
  'card',
  'button',
];
const defaultStyle = styleOptions[0];

function AccordionStyle() {
  const editor = useTPlateEditorRef();

  const currentStyle = useDreamEditorStore(() => {
    const entry = getSelectedElementEntry<AccordionElement>(editor);

    if (!entry) return defaultStyle;

    const accordionElement = entry[0].type === ELEMENT_ACCORDION
      ? entry[0]
      : getAboveNode<AccordionElement>(editor, {
        at: entry[1],
        match: (n) => n.type === ELEMENT_ACCORDION,
      })?.[0];

    return accordionElement?.style || defaultStyle;
  });

  const handleAccordionStyleChange = (
    value: NonNullable<AccordionElement['style']>,
  ) => {
    const entry = getSelectedElementEntry(editor);

    if (!entry) return;

    const [element, path] = entry;
    const accordionPath = element.type === ELEMENT_ACCORDION
      ? path
      : getAboveNode(editor, {
        at: path,
        match: (n) => n.type === ELEMENT_ACCORDION,
      })?.[1];

    if (accordionPath) {
      setAccordionStyle(editor, value, { at: accordionPath });
    }
  };

  return (
    <div className={toolbarStyles.section}>
      <span className={toolbarStyles.label}>Accordion Style</span>
      <Dropdown
        triggerLabel={<div className="is-capitalized">{currentStyle}</div>}
      >
        {styleOptions.map((s) => (
          <DropdownItem key={s}>
            <div
              role="none"
              className="is-flex is-align-items-center is-capitalized"
              onClick={() => handleAccordionStyleChange(s)}
            >
              {illustrations[s]}
              <span>{s}</span>
            </div>
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  );
}

export default memo(AccordionStyle);
